/**
 * Layout for exit tickets: a sheet cut into 2–8 identical tickets, each with a title,
 * name and date lines, an optional topic, prompts with answer lines, and an optional
 * rating row. Everything is millimetres on a page from `pageGeometry`.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type FontSpec,
  fitLines,
  type TextMeasure,
} from "@/lib/tool-kit/printables/text";

export type TicketsPerPage = 2 | 3 | 4 | 6 | 8;
export type RatingStyle = "none" | "scale" | "traffic";

export const TICKET_COUNTS: readonly TicketsPerPage[] = [2, 3, 4, 6, 8];
export const MAX_PROMPTS = 6;
export const MAX_ANSWER_LINES = 8;

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

const GRIDS: Record<TicketsPerPage, { cols: number; rows: number }> = {
  2: { cols: 1, rows: 2 },
  3: { cols: 1, rows: 3 },
  4: { cols: 2, rows: 2 },
  6: { cols: 2, rows: 3 },
  8: { cols: 2, rows: 4 },
};

/** The ticket cells, row by row, filling the page's content box with no gaps. */
export function ticketCells(
  geometry: PageGeometry,
  perPage: TicketsPerPage
): Box[] {
  const { cols, rows } = GRIDS[perPage];
  const width = geometry.contentWidth / cols;
  const height = geometry.contentHeight / rows;
  const cells: Box[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      cells.push({
        x: r2(geometry.left + col * width),
        y: r2(geometry.top + row * height),
        width: r2(width),
        height: r2(height),
      });
    }
  }
  return cells;
}

/** Dashed cut lines between cells: interior vertical and horizontal edges only. */
export function cutLines(
  geometry: PageGeometry,
  perPage: TicketsPerPage
): { x1: number; y1: number; x2: number; y2: number }[] {
  const { cols, rows } = GRIDS[perPage];
  const width = geometry.contentWidth / cols;
  const height = geometry.contentHeight / rows;
  const right = geometry.left + geometry.contentWidth;
  const bottom = geometry.top + geometry.contentHeight;
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let col = 1; col < cols; col += 1) {
    const x = r2(geometry.left + col * width);
    lines.push({ x1: x, y1: geometry.top, x2: x, y2: r2(bottom) });
  }
  for (let row = 1; row < rows; row += 1) {
    const y = r2(geometry.top + row * height);
    lines.push({ x1: geometry.left, y1: y, x2: r2(right), y2: y });
  }
  return lines;
}

export interface Prompt {
  text: string;
  /** Answer lines asked for with `| n`, or null for the default. */
  lines: number | null;
}

const BAR = /\s*\|\s*(\d{1,2})\s*$/;

/** Prompts, one per line; `text | 3` asks for three answer lines. */
export function parsePrompts(raw: string): Prompt[] {
  const prompts: Prompt[] = [];
  for (const line of raw.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const match = BAR.exec(trimmed);
    if (match) {
      const lines = Math.min(MAX_ANSWER_LINES, Number(match[1]));
      prompts.push({ text: trimmed.replace(BAR, "").trim(), lines });
    } else {
      prompts.push({ text: trimmed, lines: null });
    }
  }
  return prompts.slice(0, MAX_PROMPTS);
}

export interface TicketText {
  title: string;
  topicLabel: string;
  nameLabel: string;
  dateLabel: string;
  showNameDate: boolean;
  showTopic: boolean;
  prompts: Prompt[];
  rating: RatingStyle;
  ratingPrompt: string;
  ratingLow: string;
  ratingHigh: string;
  ratingTraffic: [string, string, string];
}

export interface TicketLine {
  text: string;
  x: number;
  y: number;
  size: number;
}

export interface TicketPrompt {
  text: TicketLine[];
  /** y of each answer line. */
  answerLines: number[];
}

export interface TicketRating {
  prompt: TicketLine[];
  /** Circle centres with the label under each (the number, or the traffic-light word). */
  marks: { cx: number; cy: number; label: string }[];
  radius: number;
  lowLabel: TicketLine | null;
  highLabel: TicketLine | null;
}

export interface TicketLayout {
  box: Box;
  /** The writing area inside the cell's padding. */
  inner: Box;
  scale: number;
  title: TicketLine | null;
  nameLine: { label: TicketLine; x1: number; x2: number; y: number } | null;
  dateLine: { label: TicketLine; x1: number; x2: number; y: number } | null;
  topic: { label: TicketLine; x1: number; x2: number; y: number } | null;
  prompts: TicketPrompt[];
  rating: TicketRating | null;
  lineSpacing: number;
}

const SANS: FontSpec = { kind: "sans" };
const BOLD: FontSpec = { kind: "sans", bold: true };
const PAD = 5;
const MIN_LINE = 5;
const MAX_LINE = 9;
export const DEFAULT_LINE = 7;

function line(text: string, x: number, y: number, size: number): TicketLine {
  return { text, x: r2(x), y: r2(y), size: r2(size) };
}

/** Answer lines asked for when a prompt gives no count, by cell height. */
export function defaultAnswerLines(cellHeight: number): number {
  if (cellHeight >= 100) {
    return 3;
  }
  return cellHeight >= 65 ? 2 : 1;
}

interface Measured {
  prompt: Prompt;
  lines: string[];
  size: number;
  requested: number;
}

/** Shrinks the request evenly until every prompt's lines fit the space. */
function fitAnswerCounts(requested: number[], capacity: number): number[] {
  const counts = [...requested];
  let total = counts.reduce((sum, n) => sum + n, 0);
  while (total > capacity && total > counts.length) {
    const biggest = counts.indexOf(Math.max(...counts));
    if ((counts[biggest] ?? 0) <= 1) {
      break;
    }
    counts[biggest] = (counts[biggest] ?? 1) - 1;
    total -= 1;
  }
  return counts;
}

const MIN_SCALE = 0.5;
const SHRINK = 0.94;

/**
 * One ticket's contents in `box`. If the prompts, answer lines and rating row do not all
 * fit at the natural type size, the size steps down until they do.
 */
export function layoutTicket(
  box: Box,
  measure: TextMeasure,
  text: TicketText
): TicketLayout {
  const natural = Math.min(1.25, Math.min(box.width / 95, box.height / 65));
  let scale = Math.max(0.7, natural);
  let built = buildTicket(box, measure, text, scale);
  while (!built.fits && scale > MIN_SCALE) {
    scale *= SHRINK;
    built = buildTicket(box, measure, text, scale);
  }
  return built.layout;
}

function buildTicket(
  box: Box,
  measure: TextMeasure,
  text: TicketText,
  scale: number
): { layout: TicketLayout; fits: boolean } {
  const inner: Box = {
    x: box.x + PAD,
    y: box.y + PAD,
    width: box.width - PAD * 2,
    height: box.height - PAD * 2,
  };
  const right = inner.x + inner.width;
  let y = inner.y;

  const titleSize = 5.6 * scale;
  const title = text.title.trim()
    ? line(text.title.trim(), inner.x, y + titleSize * 0.85, titleSize)
    : null;
  if (title) {
    y += titleSize * 1.3;
  }

  const labelSize = 3.6 * scale;
  const rowHeight = labelSize * 2.3;
  const field = (label: string, x1: number, x2: number, rowY: number) => ({
    label: line(label, x1, rowY, labelSize),
    x1: r2(x1 + measure(label, SANS) * labelSize + 1.5),
    x2: r2(x2),
    y: r2(rowY + 0.8),
  });
  let nameLine: TicketLayout["nameLine"] = null;
  let dateLine: TicketLayout["dateLine"] = null;
  if (text.showNameDate) {
    const rowY = y + labelSize * 1.5;
    const split = inner.x + inner.width * 0.64;
    nameLine = field(text.nameLabel, inner.x, split - 4, rowY);
    dateLine = field(text.dateLabel, split, right, rowY);
    y += rowHeight;
  }
  let topic: TicketLayout["topic"] = null;
  if (text.showTopic) {
    topic = field(text.topicLabel, inner.x, right, y + labelSize * 1.5);
    y += rowHeight;
  }
  y += 1.5 * scale;

  const promptSize = 4 * scale;
  const measured: Measured[] = text.prompts.map((prompt) => {
    const block = fitLines(
      measure,
      prompt.text,
      BOLD,
      inner.width,
      promptSize,
      promptSize * 0.75,
      2
    );
    return {
      prompt,
      lines: block.lines,
      size: block.size,
      requested: prompt.lines ?? defaultAnswerLines(box.height),
    };
  });
  const rating = text.rating !== "none";
  const ratingHeight = rating
    ? ratingBlockHeight(scale, text.ratingPrompt.trim().length > 0)
    : 0;
  const textHeight = measured.reduce(
    (sum, m) => sum + m.lines.length * m.size * 1.25 + 1.5,
    0
  );
  const bottom = inner.y + inner.height;
  const free = bottom - y - textHeight - ratingHeight;
  const count = measured.length;
  const capacity = Math.max(count, Math.floor(free / MIN_LINE));
  const counts = fitAnswerCounts(
    measured.map((m) => m.requested),
    capacity
  );
  const totalLines = counts.reduce((sum, n) => sum + n, 0);
  const lineSpacing =
    totalLines > 0
      ? Math.max(MIN_LINE, Math.min(MAX_LINE, free / totalLines))
      : DEFAULT_LINE;

  const prompts: TicketPrompt[] = [];
  measured.forEach((m, index) => {
    const textLines = m.lines.map((value, i) =>
      line(value, inner.x, y + m.size * 0.9 + i * m.size * 1.25, m.size)
    );
    y += m.lines.length * m.size * 1.25 + 1.5;
    const answerLines: number[] = [];
    for (let i = 0; i < (counts[index] ?? 0); i += 1) {
      y += lineSpacing;
      answerLines.push(r2(y));
    }
    prompts.push({ text: textLines, answerLines });
  });

  const layout: TicketLayout = {
    box,
    inner,
    scale,
    title,
    nameLine,
    dateLine,
    topic,
    prompts,
    rating: rating
      ? layoutRating(inner, bottom, ratingHeight, measure, text, scale)
      : null,
    lineSpacing: r2(lineSpacing),
  };
  return { layout, fits: y <= bottom - ratingHeight + 0.01 };
}

function ratingBlockHeight(scale: number, hasPrompt: boolean): number {
  return (hasPrompt ? 5.5 * scale : 0) + 14 * scale;
}

function layoutRating(
  inner: Box,
  bottom: number,
  height: number,
  measure: TextMeasure,
  text: TicketText,
  scale: number
): TicketRating {
  const top = bottom - height;
  const promptSize = 3.8 * scale;
  const hasPrompt = text.ratingPrompt.trim().length > 0;
  const prompt = hasPrompt
    ? fitLines(
        measure,
        text.ratingPrompt.trim(),
        BOLD,
        inner.width,
        promptSize,
        promptSize * 0.75,
        1
      ).lines.map((value) => line(value, inner.x, top + promptSize, promptSize))
    : [];
  const radius = Math.min(3.6 * scale, inner.width / 20);
  const rowY = top + (hasPrompt ? 5.5 * scale : 0) + radius + 1;
  const labels =
    text.rating === "scale"
      ? ["1", "2", "3", "4", "5"]
      : [...text.ratingTraffic];
  const span = text.rating === "scale" ? inner.width * 0.5 : inner.width * 0.85;
  const startX =
    text.rating === "scale"
      ? inner.x + radius + 24 * scale
      : inner.x + radius + 2;
  const step =
    labels.length > 1 ? (span - radius * 2) / (labels.length - 1) : 0;
  const marks = labels.map((label, index) => ({
    cx: r2(startX + step * index),
    cy: r2(rowY),
    label,
  }));
  const small = 3.2 * scale;
  return {
    prompt,
    marks,
    radius: r2(radius),
    lowLabel:
      text.rating === "scale" && text.ratingLow.trim()
        ? line(text.ratingLow.trim(), inner.x, rowY + small * 0.35, small)
        : null,
    highLabel:
      text.rating === "scale" && text.ratingHigh.trim()
        ? line(
            text.ratingHigh.trim(),
            startX + span - radius + 3,
            rowY + small * 0.35,
            small
          )
        : null,
  };
}
